import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import Breadcrumb from "../../wrappers/breadcrumb/Breadcrumb";
import Loader from "../../components/Loader";

import { Container, Row, Col, Card, Form, Accordion } from "react-bootstrap";

import Table from "../../components/Table";
import { useTranslation } from "react-i18next";
import { createColumnHelper } from "@tanstack/react-table";

import {
  authSelector,
  getReturnInfo,
  createReturn,
} from "../../store/slices/auth-slice";
import cogoToast from "cogo-toast";
import { getCurrencySymbol } from "../../helpers/common";
import { startSubstituteOrderCart } from "../../helpers/substituteOrder";

// Id of the option whose description matches `label` (case-insensitive), "" if none
const findOptionId = (options = [], idField, label) =>
  options.find((o) => o.description?.trim().toUpperCase() === label)?.[idField] || "";

// isSubstitution: the return is followed straight away by a substitute order
const CreateReturn = ({ isSubstitution = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {t,i18n} = useTranslation();
  const { pathname } = useLocation();
  const { orderId } = useParams();

  const { isLoading } = useSelector(authSelector);

  const [returnInfo, setReturnInfo] = useState({
    amountAlreadyRefunded: 0,
    orderTotal: 0,
    orderValue: "",
    returnValue: "",
    returnAllowance: "",
    currencyUom: "EUR",
    destinationFacilityId: "",
    fromParty: "",
    placingParty: "",
    shippingAddress: null,
    returnableItems: [],
    returnedItems: [],
  });


  useEffect(() => {
    if (!orderId) return;
    fetchReturnInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const fetchReturnInfo = async () => {
    dispatch(
      getReturnInfo({
        orderId,
        isSubstitution,
        setReturnInfo: (data) => {
          setReturnInfo({
            amountAlreadyRefunded: data.amountAlreadyRefunded || 0,
            orderTotal: data.orderTotal || 0,
            currencyUom: data.currencyUom || "EUR",
            destinationFacilityId: data.destinationFacilityId || "",
            fromParty: data.fromParty || "",
            placingParty: data.placingParty || "",
            shippingAddress: data.shippingAddress || null,
            returnValue: data?.returnValue || "",
            returnAllowance: data?.returnAllowance ?? "",
            orderValue: data?.orderValue || "",
            returnableItems: (data.returnAbleItems || []).map((item, idx) => ({
              id: item.productId,
              productId: item.productId,
              description: item.productName,
              parentInternalName: item.parentInternalName || "",
              sku: item.sku,
              ean: item.ean,
              color: item.color || "",
              size: item.size || "",
              orderQty: item.orderQty,
              orderId: item.orderId,
              orderItemSeqId: item.orderItemSeqId,
              returnQty: item.returnQty || 0,
              returnedQty: item.returnedQty || 0,
              availableReturnQty: item.returnQty,
              unitPrice: item.unitPrice,
              reasons: (item.reasons || []).map((r) => ({
                value: r.returnReasonId,
                label: r.description,
              })),
              types: (item.types || []).map((t) => ({
                value: t.returnTypeId,
                label: t.description,
              })),
              // Substitutions only: default "Normal Return" reason and "Refund" type, when available for the item
              reason: isSubstitution ? findOptionId(item.reasons, "returnReasonId", "NORMAL RETURN") : "",
              type: isSubstitution ? findOptionId(item.types, "returnTypeId", "REFUND") : "",
              selected: false,
            })),
            returnedItems: (data.returns || []).map((ret) => ({
              returnId: ret.returnId,
              returnItems: ret.returnItems,
              status: ret.statusDes,
              statusId: ret.statusId,
            })),
          });
        },
      })
    );
  };

  const {
    orderTotal,
    orderValue,
    returnValue,
    returnAllowance,
    currencyUom,
    destinationFacilityId,
    fromParty,
    placingParty,
    shippingAddress,
    returnableItems,
    returnedItems,
  } = returnInfo;

  const handleSelectItem = useCallback((id) => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      ),
    }));
  }, []);

  // Derived from the rows, so it also follows single-row (de)selection
  const allSelected =
    returnableItems.length > 0 && returnableItems.every((item) => item.selected);

  // Reads the current rows instead of a separate flag: the memoized columns
  // would otherwise keep a stale value and never deselect.
  const handleSelectAll = useCallback(() => {
    setReturnInfo((prev) => {
      const everySelected =
        prev.returnableItems.length > 0 &&
        prev.returnableItems.every((item) => item.selected);
      return {
        ...prev,
        returnableItems: prev.returnableItems.map((item) => ({
          ...item,
          selected: !everySelected,
        })),
      };
    });
  }, []);

  const handleReturnQtyChange = useCallback((id, value) => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) =>
        item.id === id ? { ...item, returnQty: Number(value) } : item
      ),
    }));
  }, []);

  const handleReasonChange = useCallback((id, reasonValue) => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) =>
        item.id === id ? { ...item, reason: reasonValue } : item
      ),
    }));
  }, []);

  const handleTypeChange = useCallback((id, typeValue) => {
    setReturnInfo((prev) => ({
      ...prev,
      returnableItems: prev.returnableItems.map((item) =>
        item.id === id ? { ...item, type: typeValue } : item
      ),
    }));
  }, []);

  const handleReturnSelected = () => {
    const selectedItems = returnableItems.filter((item) => item.selected);
    if (!selectedItems.length) {
      cogoToast.error(t("No items selected!"), {
        position: "top-right",
        hideAfter: 5,
      });
      return;
    }

    if (selectedItems.some((item) => item.returnQty === 0)) {
      cogoToast.error(t("Quantity must be greater than 0!"), {
        position: "top-right",
        hideAfter: 5,
      });
      return;
    }

    // Reason and refund method are required on every returned item (the backoffice return pages expect them)
    if (selectedItems.some((item) => !item.reason || !item.type)) {
      cogoToast.error(t("Select the return reason and the refund method for every selected item!"), {
        position: "top-right",
        hideAfter: 5,
      });
      return;
    }

    const orderReturnItemList = selectedItems.map((item) => ({
      returnQuantity: item.returnQty,
      orderItemSeqId: item.orderItemSeqId,
      productId: item.productId,
      description: item.description,
      returnReasonId: item.reason,
      returnPrice: item.unitPrice,
      returnItemTypeId: "RET_FPROD_ITEM",
      needsInventoryReceive: "N",
      returnTypeId: item.type || "",
      orderId,
    }));

    const payload = {
      returnHeader: {
        orderId,
        returnHeaderTypeId: "CUSTOMER_RETURN",
        toPartyId: fromParty,
        fromPartyId: placingParty,
        destinationFacilityId,
        currencyUomId: currencyUom || "",
      },
      orderReturnItemList,
      isSubstitution: isSubstitution ? "Y" : "N",
    };
    dispatch(
      createReturn({
        data: payload,
        callback: (returnId) => {
          if (isSubstitution) {
            startSubstituteOrder(returnId);
            return;
          }
          fetchReturnInfo();
        },
      })
    );
  };

  const startSubstituteOrder = (returnId) => {
    if (!returnId) {
      cogoToast.error(t("Unable to create the substitute order"), {
        position: "top-right",
        hideAfter: 5,
      });
      fetchReturnInfo();
      return;
    }

    startSubstituteOrderCart({ dispatch, navigate, returnId, orderId });
  };

  const returnableColumnHelper = createColumnHelper();

  const returnableColumns = useMemo(
    () => [
      returnableColumnHelper.display({
        id: "select",
        header: () => (
          <div className="custom-checkbox">
            <ul>
              <li>
                <label className="sidebar-widget-list-left">
                  <input
                    type="checkbox"
                    className={allSelected ? "active" : ""}
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  <span className="checkmark" />
                </label>
              </li>
            </ul>
          </div>
        ),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="custom-checkbox">
              <ul>
                <li>
                  <label className="sidebar-widget-list-left">
                    <input
                      type="checkbox"
                      checked={item.selected}
                      className={item.selected ? "active" : ""}
                      onChange={() => handleSelectItem(item.id)}
                    />
                    <span className="checkmark" />
                  </label>
                </li>
              </ul>
            </div>
          );
        },
      }),
      // Parent (model) of a variant: internal name only; "-" for non-variants
      returnableColumnHelper.accessor("parentInternalName", {
        header: t("Model"),
        cell: (info) => info.getValue() || "-",
      }),
      returnableColumnHelper.accessor("description", {
        header: t("Name"),
        cell: (info) => info.getValue() || "",
      }),
      // SKU column hidden on request (not needed by the users); the data is still returned by getReturnInfo
      // returnableColumnHelper.accessor("sku", {
      //   header: t("SKU"),
      //   cell: (info) => info.getValue(),
      // }),
      returnableColumnHelper.accessor("color", {
        header: t("Color"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.accessor("size", {
        header: t("Size"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.accessor("orderQty", {
        header: t("Order Qty"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.display({
        id: "returnQty",
        header: t("Returnable Qty"),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="billing-info-wrap">
              <div className="billing-info">
                <input
                  type="number"
                  placeholder="0"
                  max={item.availableReturnQty}
                  value={item.returnQty === 0 ? "" : item.returnQty}
                  // onChange={(e) =>
                  //   handleReturnQtyChange(item.id, e.target.value)
                  // }
                  onChange={(e) => {
                    const value = e.target.value;
                    handleReturnQtyChange(
                      item.id,
                      value === ""
                        ? 0
                        : Math.min(Number(value), item.availableReturnQty)
                    );
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "") {
                      handleReturnQtyChange(item.id, 0);
                    }
                  }}
                  min="0"
                />
              </div>
            </div>
          );
        },
      }),
      returnableColumnHelper.accessor("returnedQty", {
        header: t("Returned Qty"),
        cell: (info) => info.getValue(),
      }),
      returnableColumnHelper.accessor("unitPrice", {
        header: t("Unit Price"),
        cell: (info) =>
          `${getCurrencySymbol(currencyUom)}${info.getValue().toFixed(2)}`,
      }),
      returnableColumnHelper.display({
        id: "reason",
        header: t("Reason"),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Form.Select
              value={item.reason}
              isInvalid={item.selected && !item.reason}
              onChange={(e) => handleReasonChange(item.id, e.target.value)}
              style={{ minWidth: "150px" }}
            >
              <option value="">{t("Select Reason")}</option>
              {item.reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </Form.Select>
          );
        },
      }),
      returnableColumnHelper.display({
        id: "type",
        header: "Type",
        cell: ({ row }) => {
          const item = row.original;
          return (
            <Form.Select
              value={item.type}
              isInvalid={item.selected && !item.type}
              onChange={(e) => handleTypeChange(item.id, e.target.value)}
              style={{ minWidth: "120px" }}
            >
              <option value="">{t("Select Type")}</option>
              {item.types.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </Form.Select>
          );
        },
      }),
    ],
    [
      i18n?.language,
      allSelected,
      currencyUom,
      // handleSelectAll,
      // handleSelectItem,
      // handleReturnQtyChange,
      // handleReasonChange,
      // handleTypeChange,
      // returnableColumnHelper,
    ]
  );

  const returnedColumnHelper = createColumnHelper();
  const returnedColumns = useMemo(
    () => [
      returnedColumnHelper.accessor("returnId", {
        header: t("Return ID"),
        cell: (info) => {
          const returnId = info.getValue();
          return <Link to={`/orders/${orderId}/${returnId}`}>{returnId}</Link>;
        },
      }),
      returnedColumnHelper.accessor("", {
        header: t("Return From Party"),
        cell: () => placingParty,
      }),
      returnedColumnHelper.accessor("", {
        header: t("Destination Facility"),
        cell: () => destinationFacilityId,
      }),
      returnedColumnHelper.accessor("status", {
        header: t("Status"),
        cell: (info) => info.getValue(),
      }),
    ],
    [orderId, placingParty, destinationFacilityId, returnedColumnHelper,i18n?.language]
  );

  // Already returned = gross allowance - remaining returnable (in cents, no float drift)
  const alreadyReturnedTotal =
    returnAllowance === "" || returnValue === ""
      ? null
      : (Math.round(Number(returnAllowance) * 100) -
          Math.round(Number(returnValue) * 100)) /
        100;

  const pageTitle = isSubstitution ? "Create Substitution" : "Create Return";

  return (
    <Loader loading={isLoading}>
      <SEO
        titleTemplate={t(pageTitle)}
        description={t("Create a return for an order.")}
      />
      <LayoutOne headerTop="visible">
        <Breadcrumb
          pages={[
            { label: t("Home"), path: process.env.PUBLIC_URL + "/" },
            { label: t(pageTitle), path: process.env.PUBLIC_URL + pathname },
          ]}
        />

        <div className="cart-main-area pt-20 pb-100">
          <Container fluid className="cart-order-container">
            <Row>
              <Col>
                <Card>
                  <Card.Header>
                    <h3 className="mb-0">
                      {t(
                        isSubstitution
                          ? "Create Substitution for Order"
                          : "Create Return for Order"
                      )}{" "}
                      {orderId}
                    </h3>
                  </Card.Header>

                  {/* <Card.Body>
                    <Row>
                      <Col md={8} className="mb-3">
                        <Row>
                          <Col>
                            <Row>
                              <strong style={{ fontWeight: 600 }}>
                                Order Total:{" "}
                              </strong>
                              {getCurrencySymbol(currencyUom) +
                                orderTotal.toFixed(2)}
                            </Row>
                            <Row>
                              <strong style={{ fontWeight: 600 }}>
                                Order Value:{" "}
                              </strong>
                              {getCurrencySymbol(currencyUom) +
                                orderTotal.toFixed(2)}
                            </Row>
                          </Col>
                          <Col>
                            <Row>
                              <strong style={{ fontWeight: 600 }}>
                                Already Refunded:{" "}
                              </strong>
                              {getCurrencySymbol(currencyUom) +
                                amountAlreadyRefunded.toFixed(2)}
                            </Row>
                            <Row>
                              <strong style={{ fontWeight: 600 }}>
                                Return Value:{" "}
                              </strong>
                              {getCurrencySymbol(currencyUom) +
                                amountAlreadyRefunded.toFixed(2)}
                            </Row>
                          </Col>
                        </Row>
                      </Col>

                      <Col md={4} className="mb-3">
                        {shippingAddress && (
                          <div>
                            <strong style={{ fontWeight: 600 }}>
                              Ship From Address
                            </strong>
                            <p className="mb-0">{shippingAddress.toName}</p>
                            <p className="mb-0">{shippingAddress.address1}</p>
                            <p className="mb-0">
                              {shippingAddress.city},{" "}
                              {shippingAddress.stateProvinceGeoId}{" "}
                              {shippingAddress.postalCode}
                            </p>
                            <p className="mb-0">
                              {shippingAddress.countryGeoId}
                            </p>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Card.Body> */}
                  <Card.Body>
                    <Row className="gy-2">
                      {/* ❶ Left ­­­—— order totals */}
                      <Col md={4}>
                        <div>
                          <strong style={{ fontWeight: 600 }}>
                            {t("Order")}&nbsp;{t("Total")}:&nbsp;
                          </strong>
                          {getCurrencySymbol(currencyUom)}
                          {orderTotal.toFixed(2)}
                        </div>
                        
                      </Col>

                      {/* ❷ Middle ­­­—— refund info */}
                      <Col md={4}>
                        <div>
                          <strong style={{ fontWeight: 600 }}>
                            {t("Already Returned Total")}:&nbsp;
                          </strong>
                          {alreadyReturnedTotal === null
                            ? t("N/A")
                            : getCurrencySymbol(currencyUom) +
                              alreadyReturnedTotal.toFixed(2)}
                        </div>
                        {/* Gross allowance for the order, before any existing return */}
                        <div>
                          <strong style={{ fontWeight: 600 }}>
                            {t("Maximum Returnable Value")}:&nbsp;
                          </strong>
                          {returnAllowance !== ""
                            ? getCurrencySymbol(currencyUom) +
                              Number(returnAllowance).toFixed(2)
                            : t("N/A")}
                        </div>
                        {/* What is still returnable, net of existing returns */}
                        <div>
                          <strong style={{ fontWeight: 600 }}>
                            {t("Remaining Returnable Value")}:&nbsp;
                          </strong>
                          {/* Missing or zero: nothing can be returned, show 0 in red */}
                          {Number(returnValue) > 0 ? (
                            getCurrencySymbol(currencyUom) +
                            Number(returnValue).toFixed(2)
                          ) : (
                            <span className="text-danger">
                              {getCurrencySymbol(currencyUom)}
                              {(0).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </Col>

                      {/* ❸ Right ­­­—— ship-from address */}
                      <Col md={4}>
                        {shippingAddress && (
                          <>
                            <strong style={{ fontWeight: 600 }}>
                              {t("Ship")}&nbsp;{t("From")}&nbsp;{t("Address")}
                            </strong>
                            <div>{shippingAddress.toName}</div>
                            <div>{shippingAddress.address1}</div>
                            <div>
                              {shippingAddress.city},{" "}
                              {shippingAddress.stateProvinceGeoId}&nbsp;
                              {shippingAddress.postalCode}
                            </div>
                            <div>{shippingAddress.countryGeoId}</div>
                          </>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Accordion defaultActiveKey="0" className="mt-3">
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>{t("Returnable Items")}</Accordion.Header>
                    <Accordion.Body>
                      <Table
                        data={returnableItems}
                        columns={returnableColumns}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        enablePagination={false}
                      />

                      {returnableItems?.length > 0 && (
                        <div className="btn-hover text-end mt-3">
                          <button
                            className="default-btn"
                            onClick={handleReturnSelected}
                          >
                            {t(
                              isSubstitution
                                ? "Return Selected Item(s) and Create Substitute Order"
                                : "Return Selected Item(s)"
                            )}
                          </button>
                        </div>
                      )}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

                <Accordion defaultActiveKey="0" className="mt-3">
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>{t("Returned Items")}</Accordion.Header>
                    <Accordion.Body>
                      <Table
                        data={returnedItems}
                        columns={returnedColumns}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        enablePagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>
          </Container>
        </div>
      </LayoutOne>
    </Loader>
  );
};

export default CreateReturn;
